import React, {
    createContext,
    useEffect,
    useState
} from "react";

import {
    Link,
    useLocation,
    useNavigate
} from "react-router-dom";

import { useAuth } from "../../hooks/auth/AuthContext";
import { useLabAccess } from "../../contexts/LabAccessContext";

import "./BillingCheckout.css";


const BillingCheckout = () => {

    const location = useLocation();
    const navigate = useNavigate();

    const {
        isAuthenticated,
        loading: authLoading
    } = useAuth();

    const {
        labs: accessLabs,
        loading: labAccessLoading
    } = useLabAccess();


    /* =====================================================
       URL PARAMETERS
    ===================================================== */

    const params = new URLSearchParams(location.search);

    const labId = params.get("labId");
    const planId = params.get("plan");
    const cancelled = params.get("cancelled") === "true";


    /* =====================================================
       STATE
    ===================================================== */

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");


    /* =====================================================
       LAB
    ===================================================== */

    const lab = Array.isArray(accessLabs)
        ? accessLabs.find(
            item => String(item.id) === String(labId)
        )
        : null;


    /* =====================================================
       PLAN
       Plan comes directly from backend
    ===================================================== */

    const plan = Array.isArray(lab?.plans)
        ? lab.plans.find(
            item =>
                String(item.id) === String(planId)
        )
        : null;


    /* =====================================================
       VALIDATE
    ===================================================== */

    useEffect(() => {

        if (authLoading || labAccessLoading) return;

        if (!lab) {
            setError("The selected lab could not be found.");
            return;
        }

        if (!plan) {
            setError("The selected plan could not be found.");
        }

    }, [
        authLoading,
        labAccessLoading,
        lab,
        plan
    ]);


    /* =====================================================
       CHECKOUT
    ===================================================== */

    const handleCheckout = async () => {

        if (!lab || !plan) return;

        setError("");
        setLoading(true);

        try {

            const response = await fetch(
                "/api/billing/create-checkout-session",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    credentials: "include",
                    body: JSON.stringify({
                        plan: plan.id,
                        labId: lab.id
                    })
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(
                    data.error ||
                    data.message ||
                    "Unable to start checkout."
                );
            }

            if (!data.checkout_url) {
                throw new Error(
                    "Stripe checkout URL was not returned."
                );
            }

            window.location.href = data.checkout_url;

        } catch (err) {

            console.error("Checkout error:", err);

            setError(
                err.message ||
                "Unable to start checkout."
            );

            setLoading(false);
        }
    };


    /* =====================================================
       LOADING
    ===================================================== */

    if (authLoading || labAccessLoading) {

        return (
            <div className="billing-page">
                <main className="billing-main">
                    <div className="checkout-error-page">
                        <h1>Loading...</h1>
                    </div>
                </main>
            </div>
        );
    }


    /* =====================================================
       INVALID LAB / PLAN
    ===================================================== */

    if (!lab || !plan) {

        return (
            <div className="billing-page">

                <main className="billing-main">

                    <div className="checkout-error-page">

                        <h1>Plan not found</h1>

                        <p>
                            Please return to the pricing
                            page and select a plan.
                        </p>

                        {labId && (
                            <Link
                                to={`/rect/app/labs/pricing/${labId}`}
                                className="checkout-button"
                            >
                                View Pricing
                            </Link>
                        )}

                    </div>

                </main>

            </div>
        );
    }


    /* =====================================================
       ENTERPRISE
    ===================================================== */

    if (plan.id === "enterprise") {

        return (

            <div className="billing-page">

                <header className="billing-header">

                    <div className="billing-container">

                        <Link
                            to={`/rect/app/labs/pricing/${labId}`}
                            className="back-link"
                        >
                            ← Back to pricing
                        </Link>

                    </div>

                </header>


                <main className="billing-main">

                    <div className="enterprise-checkout">

                        <div className="checkout-label">
                            ENTERPRISE
                        </div>

                        <h1>
                            Let's build your
                            robotics environment.
                        </h1>

                        <p>
                            {plan.description}
                        </p>


                        {Array.isArray(plan.features) && (

                            <ul>

                                {plan.features.map(feature => (

                                    <li key={feature}>
                                        <span>✓</span>
                                        {feature}
                                    </li>

                                ))}

                            </ul>

                        )}


                        <Link
                            to="/rect/contact"
                            className="checkout-button"
                        >
                            Contact Sales
                        </Link>


                        <Link
                            to={`/rect/app/labs/pricing/${labId}`}
                            className="enterprise-back"
                        >
                            ← View all plans
                        </Link>

                    </div>

                </main>

            </div>
        );
    }


    /* =====================================================
       PRICE
    ===================================================== */

    const price = Number(plan.price || 0);

    const formattedPrice =
        price.toLocaleString();


    /* =====================================================
       CHECKOUT
    ===================================================== */

    return (

        <div className="billing-page">


            {/* =================================================
                HEADER
            ================================================= */}

            <header className="billing-header">

                <div className="billing-container">

                    <div className="secure-header">
                        🔒 Secure Checkout
                    </div>

                </div>

            </header>


            {/* =================================================
                MAIN
            ================================================= */}

            <main className="billing-main">

                <div className="checkout-wrapper">


                    {/* =================================================
                        LEFT
                    ================================================= */}

                    <div className="checkout-information">

                        <Link
                            to={`/rect/app/labs/pricing/${labId}`}
                            className="back-link"
                        >
                            ← Back to pricing
                        </Link>


                        <div className="checkout-label">
                            YOUR PLAN
                        </div>


                        <h1>
                            {plan.name}
                        </h1>


                        <p className="checkout-description">
                            {plan.description}
                        </p>


                        <div className="feature-heading">
                            What's included
                        </div>


                        {Array.isArray(plan.features) && (

                            <ul className="checkout-features">

                                {plan.features.map(feature => (

                                    <li key={feature}>

                                        <span>✓</span>

                                        {feature}

                                    </li>

                                ))}

                            </ul>

                        )}


                        <div className="platform-note">

                            <div className="platform-note-icon">
                                🤖
                            </div>

                            <div>

                                <strong>
                                    {lab.name || "STEMX365"}
                                </strong>

                                <p>
                                    Develop and simulate
                                    space robots directly
                                    from your browser.
                                </p>

                            </div>

                        </div>

                    </div>


                    {/* =================================================
                        RIGHT
                    ================================================= */}

                    <div className="checkout-card">

                        <div className="checkout-card-header">
                            <span>
                                Order Summary
                            </span>
                        </div>


                        <div className="order-plan">

                            <div>

                                <strong>
                                    STEMX365{" "}
                                    {plan.name}
                                </strong>

                                <span>
                                    {lab.name}
                                </span>

                            </div>


                            <div className="order-price">

                                ${formattedPrice}

                                <small>
                                    / {plan.interval}
                                </small>

                            </div>

                        </div>


                        <div className="order-divider" />


                        <div className="order-row">

                            <span>
                                {plan.name} Plan
                            </span>

                            <span>
                                ${formattedPrice}
                            </span>

                        </div>


                        <div className="order-row">

                            <span>
                                Billing
                            </span>

                            <span>
                                {plan.interval === "month"
                                    ? "Monthly"
                                    : "Annual"}
                            </span>

                        </div>


                        <div className="order-divider" />


                        <div className="order-total">

                            <span>
                                Total
                            </span>

                            <strong>

                                ${formattedPrice}

                                <small>
                                    / {plan.interval}
                                </small>

                            </strong>

                        </div>


                        {/* =================================================
                            CANCELLED
                        ================================================= */}

                        {cancelled && (

                            <div className="checkout-cancelled">

                                <strong>
                                    Payment cancelled
                                </strong>

                                <span>
                                    No payment was made.
                                    You can try again whenever
                                    you're ready.
                                </span>

                            </div>

                        )}


                        {/* =================================================
                            ERROR
                        ================================================= */}

                        {error && (

                            <div className="checkout-error">

                                <span>!</span>

                                <div>
                                    {error}
                                </div>

                            </div>

                        )}


                        {/* =================================================
                            CHECKOUT
                        ================================================= */}

                        <button
                            type="button"
                            className="checkout-button"
                            onClick={handleCheckout}
                            disabled={loading}
                        >
                            {loading
                                ? "Connecting to Stripe..."
                                : "Continue to Secure Payment"}
                        </button>


                        <div className="stripe-note">
                            🔒 Secure payment powered by Stripe
                        </div>


                        <p className="terms-note">

                            By continuing, you agree to the
                            STEMX365{" "}

                            <Link to="/rect/html/terms">
                                Terms of Service
                            </Link>

                            {" "}and{" "}

                            <Link to="/rect/html/privacy">
                                Privacy Policy
                            </Link>.

                        </p>

                    </div>

                </div>

            </main>

        </div>
    );
};


export default BillingCheckout;